import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../components/Loader';
import AdvanceFilter from './AdvanceFilter';
import SideCards from './SideCards';
import BatchList from './BatchList';
import ScrollContainer from '../../components/ScrollContainerV2';
import CreateBatch from './CreateBatch';
import DeleteDocuments from './DeleteDocuments';
import BatchDetails from './BatchDetails';
import PrintFailures from './PrintFailures';
import MailRoomProvider from '../../context/mailRoomContext';
import { dateTimeConversion, getWeek } from '../../utils/javascript';
import { getBatchList, getTagsList } from '../../utils/services/mailRoomAPI';
import { Main, Container, Header, DataLoader } from './styles';
import { withTranslation } from 'react-i18next';
import { BATCH_LIST } from './translationValues';

const NAMESPACES=['BATCH_LIST'];
const pageLimit = 12;

const MailRoom = ({ t }) => {
  const [searchValue, setSearchValue] = useState(null);
  const [startAndEndDate, setStartAndEndDate] = useState(null);
  const [dateFilterApplied, setDateFilterApplied] = useState(false);
  const [batchList, setBatchList] = useState([]);
  const [batchCount, setBatchCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchingMoreData, setFetchingMoreData] = useState(false);
  const [tagValues, setAllTagList] = useState([]);
  const [lastUpdatedOn, setLastUpdatedOn] = useState(null);
  const [createBatch, setCreateBatch] = useState(false);
  const [deleteDocuments, setDeleteDocuments] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [batchData, setBatchData] = useState(null);
  const [printFailures, setPrintFailures] = useState(false);

  useEffect(() => {
    setDefaultWeek();
    fetchTagsList();
  }, []);

  useEffect(() => {
    if (startAndEndDate?.startDate && startAndEndDate?.endDate) {
      setCurrentPage(1);
      fetchBatchDetails(1);
    }
  }, [searchValue, startAndEndDate]);

  useEffect(() => {
    if (currentPage > 1) {
      setFetchingMoreData(true);
      fetchBatchDetails(currentPage);
    }
  }, [currentPage]);

  const setDefaultWeek = () => {
    let startAndEndDate = getWeek();
    let dates = {
      startDate: startAndEndDate.startDate,
      endDate: startAndEndDate.endDate,
    };
    setStartAndEndDate(dates);
  };

  const getTagDetails = data => {
    let anArray = [];
    let allTagValues = [];
    tagValues.map(li => {
      let anObject = {};
      let tagName = li.tagName;
      let tagValue = data[li.tagId];
      allTagValues = [...allTagValues, tagValue];
      anObject.tagName = tagName;
      anObject.tagValue = tagValue;
      anObject.allTagValues = allTagValues;
      anArray.push(anObject);
    });
    return anArray;
  };

  const getAllTagValues = data => {
    let allTags = getTagDetails(data);
    let allTagValues = allTags.length > 0 && allTags[allTags.length - 1].allTagValues;
    let allFilteredTags = [];
    if (allTagValues?.length > 0) {
      allTagValues.map(tag => {
        if (tag && tag?.length > 0) {
          allFilteredTags = [...allFilteredTags, ...tag?.split(',')];
        }
      });
      return allFilteredTags;
    }
  };

  const fetchTagsList = useCallback(async () => {
    let tagResp = await getTagsList();
    if (tagResp.errors && tagResp.errors.length === 0) {
      if (tagResp && tagResp.data) {
        setAllTagList(tagResp.data);
      }
    }
  }, []);

  const fetchBatchDetails = useCallback(
    async page => {
      if (page === 1) {
        setIsLoading(true);
      }
      let dateRange = startAndEndDate;
      if(!dateFilterApplied) {
        let startAndEndDate = getWeek();
        dateRange = {
          startDate: startAndEndDate.startDate,
          endDate: startAndEndDate.endDate,
        };
      }
      let payload = {
        sortorder: 'desc',
        sortby: 'createdOn',
        limit: pageLimit,
      };
      if (dateRange?.startDate && dateRange?.endDate) {
        payload.dateFrom = dateRange.startDate;
        payload.dateTo = dateRange.endDate;
      }
      payload.page = page;
      if (searchValue !== null && searchValue !== '') {
        payload.name = searchValue;
      }
      const response = await getBatchList(payload);
      if (response) {
        if (response.errors && response.errors.length === 0 && response.data) {
          setLastUpdatedOn(dateTimeConversion(new Date(), false, true, false));
          if (page && page > 1) {
            setBatchList(batchList => batchList?.concat(response?.data));
          } else {
            setBatchList(response?.data);
          }
          setBatchCount(response.dataCount);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
        setFetchingMoreData(false);
      }
    },
    [searchValue, startAndEndDate, currentPage],
  );

  const onCancel = () => {
    setBatchData(null);
    setPrintFailures(false);
  };

  const fetchMoreData = useCallback(() => {
    if (batchCount > currentPage * pageLimit) {
      setCurrentPage(currentPage => currentPage + 1);
    }
  }, [currentPage, batchCount]);

  useEffect(() => {
    if (createBatch || deleteDocuments) {
      setShowDrawer(false)
    }
  }, [createBatch, deleteDocuments])

  const onRefresh = () => {
    setBatchList([])
    setBatchCount(0);
    setCurrentPage(1);
    fetchBatchDetails(1);
  }

  return (
    <MailRoomProvider>
      <Main>
        <Container className={showDrawer ? 'collapse' : 'expand'}>
          <Header justifyContent="flex-end" className={showDrawer ? 'collapse' : 'expand'} height={'20vh'}>
            <h2 className="title">{t(BATCH_LIST.MAIL_ROOM_TITLE.key, BATCH_LIST.MAIL_ROOM_TITLE.value)}</h2>
            <SideCards
              setPrintFailures={setPrintFailures}
              lastUpdatedOn={lastUpdatedOn}
              startAndEndDate={startAndEndDate}
              setDeleteDocuments={setDeleteDocuments}
              searchValue={searchValue}
              onRefresh={onRefresh}
              dateFilterApplied={dateFilterApplied}
            />
          </Header>
          <ScrollContainer
            customClassName={`batchList ${showDrawer ? 'collapse' : 'expand'}`}
            fetchMoreData={fetchMoreData}
          >
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <BatchList
                  batchList={batchList}
                  setCreateBatch={setCreateBatch}
                  setBatchData={setBatchData}
                  getAllTagValues={getAllTagValues}
                />
                <DataLoader>
                  {fetchingMoreData
                    ? t(BATCH_LIST.LOADING.key, BATCH_LIST.LOADING.value)
                    : batchList?.length === batchCount ? (batchCount === 0 ? t(BATCH_LIST.NO_BATCH_MESSAGE.key, BATCH_LIST.NO_BATCH_MESSAGE.value) : t(BATCH_LIST.GOT_ALL_RESULT_MESSAGE.key, BATCH_LIST.GOT_ALL_RESULT_MESSAGE.value)) : ''}
                </DataLoader>
              </>
            )}
          </ScrollContainer>
        </Container>
        <AdvanceFilter
          visibility={createBatch || deleteDocuments ? false : true}
          title={t(BATCH_LIST.BATCH_FILTER_TITLE.key, BATCH_LIST.BATCH_FILTER_TITLE.value)}
          setSearchValue={setSearchValue}
          startAndEndDate={startAndEndDate}
          setStartAndEndDate={setStartAndEndDate}
          setDefaultWeek={setDefaultWeek}
          showDrawer={showDrawer}
          setShowDrawer={setShowDrawer}
          createBatch={createBatch}
          deleteDocuments={deleteDocuments}
          setDateFilterApplied={setDateFilterApplied}
        />
        {createBatch && <CreateBatch setDefaultWeek={setDefaultWeek} setCreateBatch={setCreateBatch} />}
        {deleteDocuments && <DeleteDocuments setDefaultWeek={setDefaultWeek} setDeleteDocuments={setDeleteDocuments} />}
        {batchData && (
          <BatchDetails
            batchData={batchData}
            printFailures={printFailures}
            getTagDetails={getTagDetails}
            startAndEndDate={startAndEndDate}
            onCancel={onCancel}
            tagValues={tagValues}
          />
        )}
        {printFailures && (
          <PrintFailures
            batchData={batchData}
            printFailures={printFailures}
            getTagDetails={getTagDetails}
            startAndEndDate={startAndEndDate}
            onCancel={onCancel}
            tagValues={tagValues}
            dateFilterApplied={dateFilterApplied}
          />
        )}
      </Main>
    </MailRoomProvider>
  );
};
export default withTranslation(NAMESPACES)(MailRoom);
